import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  useTheme
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { Stack } from "@mui/system";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import envConstants from "~/config/envConstants";

import useFlag from "~/config/flags";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectUserIsAdmin,
  selectUserIsWorkstationConfigurer,
  selectUsersProfileName
} from "~/redux/selectors/authSelectors";

import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useGetAutostoreGridQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetServerVersionQuery } from "~/redux/warehouse/version";

import { FulfillmentCenterSelectOld } from "./FulfillmentCenterSelectOld";
import { GridStateSynchronizer } from "./GridStateSynchronizer";
import { PrinterSettings } from "./PrinterSettings";
import { UserSettings } from "./UserSettings";
import { WorkstationPanel } from "./WorkstationPanel";

export const SettingsTabPanel = () => {
  const selectedFulfillmentCenter = useAppSelector(
    (state) => state.store.usersFulfillmentCenter
  );
  const usersName = useAppSelector(selectUsersProfileName);
  const isAutostoreUser = usersName?.includes("autostoresystem.com") ?? false;
  const areDevCheatsEnabled =
    envConstants.DEV_CHEATS_UAT === "true" ||
    envConstants.DEV_CHEATS_STAGING === "true";
  const showServerVersion = isAutostoreUser || areDevCheatsEnabled;
  const { data: serverVersion } = useGetServerVersionQuery(undefined, {
    skip: !showServerVersion
  });

  const { t } = useTranslation();

  const isAdmin = useAppSelector(selectUserIsAdmin);
  const workstation = useAppSelector(selectThisWorkstation);
  const userFc = useAppSelector((state) => state.store.usersFulfillmentCenter);

  const {
    error: selectedAutostoreGridError,
    refetch: refetchSelectedAutostoreGrid
  } = useGetAutostoreGridQuery(workstation?.autostoreGridId ?? skipToken, {
    skip: !userFc || !workstation?.autostoreGridId
  });

  // show and hide beamer selector upon mount and dismount
  useEffect(() => {
    const intervalId = setInterval(() => {
      const beamerSelector = document.getElementById("beamerSelector");
      if (beamerSelector) {
        clearInterval(intervalId);
        if (isAdmin) {
          beamerSelector.style.display = "block";
        }
      }
    }, 100);

    return () => {
      clearInterval(intervalId);
      const beamerSelector = document.getElementById("beamerSelector");

      if (beamerSelector) {
        beamerSelector.style.display = "none";
      }
    };
  }, [isAdmin]);

  const { enableSideNavSubMenus } = useFlag();
  const { palette } = useTheme();

  const isWorkstationConfigurer = useAppSelector(
    selectUserIsWorkstationConfigurer
  );

  const panel = (
    <Stack p={2} spacing={4}>
      {isWorkstationConfigurer && (
        <Stack spacing={2}>
          <Typography variant="h6">{t("fulfillment center")}</Typography>

          <FulfillmentCenterSelectOld />
          <FormControl>
            <FormLabel>{t("fulfillment center")} ID</FormLabel>
            <FormHelperText>
              {t("selected fulfillment center id description")}
            </FormHelperText>
            <Typography>
              {selectedFulfillmentCenter?.fulfillmentCenterId}
            </Typography>
          </FormControl>
          <GridStateSynchronizer />
          {!!selectedAutostoreGridError && (
            <Alert
              sx={{ gridColumn: "1/-1" }}
              variant="outlined"
              severity="error"
              action={
                <Button onClick={refetchSelectedAutostoreGrid}>Retry</Button>
              }
            >
              {getMessageFromRtkError(selectedAutostoreGridError)}
            </Alert>
          )}
        </Stack>
      )}
      {isWorkstationConfigurer && (
        <>
          <Divider />
          <WorkstationPanel />
          <Divider />
        </>
      )}

      <Stack spacing={2}>
        <Typography variant="h6">{t("nav.viewname.user settings")}</Typography>
        <UserSettings />
      </Stack>

      {isWorkstationConfigurer &&
        selectedFulfillmentCenter?.printerConfiguration === "BrowserPrint" && (
          <>
            <Divider />
            <Stack>
              <Typography variant="h6">{t("nav.viewname.printer")}</Typography>
              <PrinterSettings maxWidth="sm" />
            </Stack>
          </>
        )}

      <Divider />

      <Stack spacing={2} aria-label="version information">
        <Typography variant="h6">{t("nav.viewname.version")}</Typography>
        <Box
          display="grid"
          gridTemplateColumns="auto 1fr"
          gap={1}
          columnGap={2}
          alignItems="center"
        >
          <Typography variant="subtitle1" color="textSecondary">
            {t("client version")}
          </Typography>
          <Typography>
            {envConstants.VERSION_TAG || envConstants.VERSION || "Unknown"}
          </Typography>

          {showServerVersion && (
            <>
              <Typography variant="subtitle1" color="textSecondary">
                {t("server version")}
              </Typography>
              <Typography>
                {serverVersion?.tag || serverVersion?.hash || "Unknown"}
              </Typography>
            </>
          )}
        </Box>
      </Stack>
    </Stack>
  );

  return enableSideNavSubMenus ? (
    <Container>
      <Box bgcolor={palette.background.secondary} pb="4px" mt={2}>
        <Box bgcolor={palette.background.default} mx="4px" borderRadius="8px">
          {panel}
        </Box>
      </Box>
    </Container>
  ) : (
    panel
  );
};
